import { unreachable } from 'module/helpers/unreachable';
import { FieldType } from 'module/types/filter-field/field/field';
import { FieldFilter } from 'module/types/filter-field/filter';
import { BooleanFieldFilter } from 'module/types/filter-field/filter/boolean';
import { DateFieldFilter } from 'module/types/filter-field/filter/date';
import { EstimateFieldFilter } from 'module/types/filter-field/filter/estimate';
import { MultiSelectFieldFilter } from 'module/types/filter-field/filter/multi-select';
import { MultiUserFieldFilter } from 'module/types/filter-field/filter/multi-user';
import { NumberFieldFilter } from 'module/types/filter-field/filter/number';
import { SingleSelectFieldFilter } from 'module/types/filter-field/filter/single-select';
import { SingleUserFieldFilter } from 'module/types/filter-field/filter/single-user';
import { TextFieldFilter } from 'module/types/filter-field/filter/text';
import { Form } from 'module/ui/components/atoms/form';
import * as React from 'react';
import { BooleanCriteria } from './boolean';
import { DateCriteria } from './date';
import { EstimateCriteria } from './estimate';
import { MultiSelectCriteria } from './multi-select';
import { MultiUserCriteria } from './multi-user';
import { NumberCriteria } from './number';
import { SingleSelectCriteria } from './single-select';
import { SingleUserCriteria } from './single-user';
import { TextCriteria } from './text';
import { BaseFilterComponentProps } from '../types';

interface Props extends BaseFilterComponentProps {
  filter?: FieldFilter;
  onChange: (filter: FieldFilter) => void;
}

export const criterion = ({
  filter,
  onChange,
  getFilterOperationTypeOptions,
  getSelectEditorComponents,
  getOperationOptionRenderer,
  userOptionsGetter,
}: Props): React.ReactNode => {
  if (!filter) {
    return null;
  }

  switch (filter.field.type) {
    case FieldType.Boolean:
      return (
        <BooleanCriteria
          filter={filter as BooleanFieldFilter}
          onChange={onChange}
          getFilterOperationTypeOptions={
            getFilterOperationTypeOptions?.[FieldType.Boolean]
          }
          getOperationOptionRenderer={
            getOperationOptionRenderer?.BooleanFilterOperationType
          }
        />
      );
    case FieldType.Number:
      return (
        <NumberCriteria
          filter={filter as NumberFieldFilter}
          onChange={onChange}
          getFilterOperationTypeOptions={
            getFilterOperationTypeOptions?.[FieldType.Number]
          }
          getOperationOptionRenderer={
            getOperationOptionRenderer?.NumberFilterOperationType
          }
        />
      );
    case FieldType.Text:
    case FieldType.MultiLineText:
    case FieldType.Note:
      return (
        <TextCriteria
          filter={filter as TextFieldFilter}
          onChange={onChange}
          getFilterOperationTypeOptions={
            getFilterOperationTypeOptions?.[FieldType.Text]
          }
          getOperationOptionRenderer={
            getOperationOptionRenderer?.TextFieldFilterOperationType
          }
        />
      );
    case FieldType.Date:
      return (
        <Form.Item>
          <DateCriteria
            filter={filter as DateFieldFilter}
            onChange={onChange}
            getFilterOperationTypeOptions={
              getFilterOperationTypeOptions?.[FieldType.Date]
            }
            getOperationOptionRenderer={
              getOperationOptionRenderer?.DateFieldFilterOperationType
            }
          />
        </Form.Item>
      );
    case FieldType.SingleSelect:
      return (
        <SingleSelectCriteria
          filter={filter as SingleSelectFieldFilter}
          onChange={onChange}
          components={getSelectEditorComponents?.(filter.field)}
          getFilterOperationTypeOptions={
            getFilterOperationTypeOptions?.[FieldType.SingleSelect]
          }
          getOperationOptionRenderer={
            getOperationOptionRenderer?.SelectFilterOperationType
          }
        />
      );
    case FieldType.MultiSelect:
      return filter.field.fieldName === 'responsible' ? (
        //TODO: Add responsible criteria
        // <Form.Item>
        //   <ResponsibleCriteria
        //     filter={filter as MultiSelectFieldFilter}
        //     onChange={onChange}
        //   />
        // </Form.Item>
        unreachable(filter.field as never)
      ) : (
        <MultiSelectCriteria
          filter={filter as MultiSelectFieldFilter}
          onChange={onChange}
          components={getSelectEditorComponents?.(filter.field)}
          getFilterOperationTypeOptions={
            getFilterOperationTypeOptions?.[FieldType.MultiSelect]
          }
          getOperationOptionRenderer={
            getOperationOptionRenderer?.SelectFilterOperationType
          }
        />
      );
    case FieldType.SingleUser:
      return (
        <SingleUserCriteria
          filter={filter as SingleUserFieldFilter}
          onChange={onChange}
          components={getSelectEditorComponents?.(filter.field)}
          optionsGetter={userOptionsGetter}
        />
      );
    case FieldType.MultiUser:
      return (
        <MultiUserCriteria
          filter={filter as MultiUserFieldFilter}
          onChange={onChange}
          components={getSelectEditorComponents?.(filter.field)}
          optionsGetter={userOptionsGetter}
          show={1}
        />
      );
    case FieldType.Estimate:
      return (
        <Form.Item>
          <EstimateCriteria
            filter={filter as EstimateFieldFilter}
            onChange={onChange}
          />
        </Form.Item>
      );
    default:
      return unreachable(filter.field as never);
  }
};
